// BottomNav — fixed mobile bottom navigation (68px tall).
//
// Phase 1 of VISUAL_REFACTOR_PLAN.md fleshes out what was a stub into the
// role-conditional nav that mirrors the desktop Sidebar. Each role gets
// four nav items plus the centre Scan/Pay button (the unified QR entry
// point). Active state uses the role accent.
import { NavLink } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  Calendar,
  ChefHat,
  Compass,
  FileCheck2,
  Flag,
  LayoutDashboard,
  QrCode,
  Receipt,
  ShoppingBag,
  Ticket,
  User,
  Users,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useRoleStore } from '@/store/role.store'
import type { UserRole } from '@/types/user.types'
import { isEnabled, type FeatureFlag } from '@/config/features'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
  /** Hide the entry when this feature flag is OFF. */
  flag?: FeatureFlag
}

// Each role surfaces its four most-used destinations — the centre slot is
// always reserved for Scan/Pay so we render two items on each side of it.
const NAV_ITEMS: Record<UserRole, NavItem[]> = {
  admin: [
    { to: '/admin', label: 'Home', icon: LayoutDashboard, end: true },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/kyc', label: 'KYC', icon: FileCheck2 },
    { to: '/admin/disputes', label: 'Disputes', icon: Flag },
  ],
  organiser: [
    { to: '/organiser', label: 'Home', icon: LayoutDashboard, end: true },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/organiser/revenue', label: 'Revenue', icon: Receipt },
    { to: '/profile', label: 'Profile', icon: User },
  ],
  vendor: [
    { to: '/vendor', label: 'Home', icon: LayoutDashboard, end: true },
    { to: '/vendor/menu', label: 'Menu', icon: ChefHat },
    { to: '/vendor/orders/live', label: 'Orders', icon: ShoppingBag, flag: 'VENDOR_ORDER_BOARD' },
    { to: '/vendor/earnings', label: 'Earnings', icon: Wallet, flag: 'VENDOR_EARNINGS_DASHBOARD' },
  ],
  attendee: [
    { to: '/events', label: 'Discover', icon: Compass },
    { to: '/tickets', label: 'Tickets', icon: Ticket },
    { to: '/wallet', label: 'Wallet', icon: Wallet, flag: 'ATTENDEE_WALLET' },
    { to: '/profile', label: 'Profile', icon: User },
  ],
}

export default function BottomNav() {
  const { activeRole } = useRoleStore()
  const allItems = NAV_ITEMS[activeRole] ?? NAV_ITEMS.attendee
  const items = allItems.filter((item) => !item.flag || isEnabled(item.flag))

  // Centre Scan/Pay slot — the visual centrepiece of the bar. Hidden
  // entirely when VENDOR_PAYMENTS is off (Phase 1 has no QR payments).
  const showScan = isEnabled('VENDOR_PAYMENTS')
  const scanLabel = activeRole === 'vendor' ? 'Pay' : 'Scan'

  // Split items 2-and-2 around the centre QR button. When VENDOR_PAYMENTS
  // is off we collapse to a flat 4-up layout (no Scan button), so the
  // split is purely cosmetic and either layout works.
  const left = showScan ? items.slice(0, 2) : items.slice(0, items.length)
  const right = showScan ? items.slice(2, 4) : []

  return (
    <nav
      className="flex bg-[var(--surface)] border-t border-[var(--border-subtle)] pb-3 pt-1.5"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
    >
      {left.map((item) => (
        <BottomNavLink key={item.to} {...item} />
      ))}

      {/* Centre Scan/Pay — raised, accent-coloured. Phase 1 has no QR
          payments yet, so we hide this entirely when VENDOR_PAYMENTS is off. */}
      {showScan && (
        <div className="flex-1 flex flex-col items-center">
          <button
            type="button"
            aria-label={scanLabel}
            className="w-12 h-12 rounded-full flex items-center justify-center -mt-5 border-[3px] border-[var(--surface)] bg-[var(--accent)] text-[var(--text-on-accent)] shadow-[var(--shadow-panel)]"
          >
            <QrCode className="w-5 h-5" strokeWidth={2.2} />
          </button>
          <span className="text-[10px] text-[var(--text-muted)] mt-0.5 font-medium">{scanLabel}</span>
        </div>
      )}

      {right.map((item) => (
        <BottomNavLink key={item.to} {...item} />
      ))}
    </nav>
  )
}

function BottomNavLink({ to, label, icon: Icon, end }: NavItem) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        clsx(
          'flex-1 flex flex-col items-center justify-center gap-0.5 py-1 transition-colors',
          isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]',
        )
      }
    >
      <Icon className="w-5 h-5" strokeWidth={2} />
      <span className="text-[10px] font-semibold">{label}</span>
    </NavLink>
  )
}
